import React, { useState } from "react";
import styled from "styled-components";
import { FaMicrophone, FaHeart, FaCalendar, FaEnvelope } from "react-icons/fa";
import {
  colors,
  fonts,
  spacing,
  borderRadius,
  shadows,
  transitions,
} from "../styles/branding";
import { useFormSubmit } from "../hooks/useFormSubmit";

const CustomSpeechesContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  background: ${colors.gradient.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-family: ${fonts.heading};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
  font-family: ${fonts.body};
`;

const ContentSection = styled.section`
  padding: 5rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: ${colors.inkBlack};
    font-family: ${fonts.heading};
    font-weight: 600;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${colors.coolTaupe};
    margin-bottom: 1.5rem;
    font-family: ${fonts.body};
  }
`;

const ImageContainer = styled.div`
  img {
    width: 100%;
    border-radius: ${borderRadius.md};
    box-shadow: ${shadows.lg};
  }
`;

const ServicesSection = styled.section`
  padding: 5rem 2rem;
  background: ${colors.paleMauve};
`;

const ServicesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
`;

const ServiceCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: ${borderRadius.md};
  text-align: center;
  box-shadow: ${shadows.md};
`;

const ServiceIcon = styled.div`
  font-size: 3rem;
  color: ${colors.dustyRose};
  margin-bottom: 1rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 500;
`;

const ServiceText = styled.p`
  color: ${colors.coolTaupe};
  line-height: 1.6;
  font-family: ${fonts.body};
`;

const BookingSection = styled.section`
  padding: 5rem 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const BookingContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BookingTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: ${colors.inkBlack};
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const BookingForm = styled.form`
  background: white;
  padding: 3rem;
  border-radius: ${borderRadius.md};
  box-shadow: ${shadows.lg};

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 2rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: ${colors.inkBlack};
    font-family: ${fonts.body};
  }

  input,
  textarea,
  select {
    width: 100%;
    padding: 1rem;
    border: 2px solid #eee;
    border-radius: 8px;
    font-size: 1rem;
    font-family: ${fonts.body};
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${colors.dustyRose};
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }

  @media (max-width: 768px) {
    input,
    textarea,
    select {
      padding: 1.2rem;
      font-size: 1.1rem;
    }
  }
`;

const SubmitButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: ${borderRadius.lg};
  font-weight: bold;
  font-size: 1rem;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};
  width: 100%;

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: ${colors.paleMauve};
  border-radius: ${borderRadius.lg};
  margin-top: 2rem;
`;

const ThankYouTitle = styled.h2`
  font-size: 2rem;
  color: ${colors.inkBlack};
  margin-bottom: 1.5rem;
  font-family: ${fonts.heading};
  font-weight: 600;
`;

const ThankYouText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${colors.coolTaupe};
  margin-bottom: 1.5rem;
  font-family: ${fonts.body};
  font-style: italic;
`;

const BackButton = styled.button`
  background: ${colors.dustyRose};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${borderRadius.md};
  font-weight: bold;
  font-family: ${fonts.body};
  cursor: pointer;
  transition: ${transitions.normal};

  &:hover {
    background: ${colors.coolTaupe};
    transform: translateY(-2px);
  }
`;

const CustomSpeeches = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    eventType: "",
    date: "",
    location: "",
    audience: "",
    message: "",
  });
  const [showThankYou, setShowThankYou] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const { submitForm, isSubmitting, submitStatus, resetStatus } =
    useFormSubmit();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Format the message with all form fields except name and email, two newlines between each
    const formattedMessage = `Phone: ${formData.phone}\n\nEvent Type: ${formData.eventType}\n\nDate: ${formData.date}\n\nLocation: ${formData.location}\n\nAudience Size: ${formData.audience}\n\nAdditional Details: ${formData.message}`;

    const result = await submitForm(
      "Custom Speech",
      formData.name,
      formData.email,
      formattedMessage
    );

    if (result.success) {
      setShowThankYou(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        eventType: "",
        date: "",
        location: "",
        audience: "",
        message: "",
      });
      resetStatus();
    } else {
      alert(
        "Sorry, there was an error sending your message. Please try again."
      );
    }
  };

  const handleBackToForm = () => {
    setShowThankYou(false);
  };

  return (
    <CustomSpeechesContainer>
      <HeroSection>
        <HeroTitle>Custom Speeches</HeroTitle>
        <HeroSubtitle>
          Let Shana craft a personalized speech that captures the essence of
          your special moment
        </HeroSubtitle>
      </HeroSection>

      <ContentSection>
        <TextContent>
          <h2>Personalized Speeches for Every Occasion</h2>
          <p>
            From heartfelt wedding toasts to inspiring corporate addresses,
            Shana crafts custom speeches that resonate with your audience and
            capture the unique spirit of your event.
          </p>
          <p>
            Each speech is written specifically for you, incorporating
            personal stories, meaningful anecdotes, and the perfect tone for
            your occasion. Whether you need a touching tribute, a motivational
            message, or a celebration of love, Shana will create words that
            will be remembered long after the event.
          </p>
          <p>
            With over 1,000 events under her belt, Shana understands what
            makes a speech truly special. Let her help you find the perfect
            words for your moment.
          </p>
        </TextContent>
      </ContentSection>

      <ServicesSection>
        <BookingTitle>Speech Types</BookingTitle>
        <ServicesGrid>
          <ServiceCard>
            <ServiceIcon>
              <FaHeart />
            </ServiceIcon>
            <ServiceTitle>Weddings & Celebrations</ServiceTitle>
            <ServiceText>
              Toasts, vows, and speeches for weddings, anniversaries, and
              romantic celebrations that capture the love and joy of your
              special day.
            </ServiceText>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>
              <FaCalendar />
            </ServiceIcon>
            <ServiceTitle>Life Milestones</ServiceTitle>
            <ServiceText>
              Speeches for baby showers, bar/bat mitzvahs, graduations, and
              other life-changing moments that deserve beautiful words.
            </ServiceText>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>
              <FaMicrophone />
            </ServiceIcon>
            <ServiceTitle>Corporate & Professional</ServiceTitle>
            <ServiceText>
              Keynote addresses, grand opening speeches, and professional
              presentations that inspire and engage your audience.
            </ServiceText>
          </ServiceCard>
        </ServicesGrid>
      </ServicesSection>

      <BookingSection>
        <BookingContainer>
          <BookingTitle>Book Your Custom Speech</BookingTitle>
          {showThankYou ? (
            <ThankYouMessage>
              <ThankYouTitle>Thank You</ThankYouTitle>
              <ThankYouText>
                "Words have power, and your words have found their way to me.
                Thank you for entrusting me with your story."
              </ThankYouText>
              <ThankYouText>
                Shana will carefully review your details and reach out within 24
                hours to discuss how we can craft the perfect speech for your
                special moment. Your vision deserves words that will be
                remembered.
              </ThankYouText>
              <BackButton onClick={handleBackToForm}>
                Book Another Speech
              </BackButton>
            </ThankYouMessage>
          ) : (
            <BookingForm onSubmit={handleSubmit}>
              <FormGroup>
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="eventType">Type of Speech *</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a speech type</option>
                  <option value="wedding">Wedding Speech</option>
                  <option value="baby-shower">Baby Shower Speech</option>
                  <option value="bar-mitzvah">Bar/Bat Mitzvah Speech</option>
                  <option value="graduation">Graduation Speech</option>
                  <option value="corporate">Corporate Speech</option>
                  <option value="other">Other</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="date">Event Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="location">Event Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, State or Venue"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="audience">Expected Audience Size</label>
                <select
                  id="audience"
                  name="audience"
                  value={formData.audience}
                  onChange={handleInputChange}
                >
                  <option value="">Select audience size</option>
                  <option value="1-50">1-50 people</option>
                  <option value="51-100">51-100 people</option>
                  <option value="101-250">101-250 people</option>
                  <option value="251-500">251-500 people</option>
                  <option value="500+">500+ people</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">Speech Details</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell Shana about your event, the people involved, any specific themes or messages you'd like included..."
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                <FaEnvelope /> {isSubmitting ? "Sending..." : "Send Inquiry"}
              </SubmitButton>
            </BookingForm>
          )}
        </BookingContainer>
      </BookingSection>
    </CustomSpeechesContainer>
  );
};

export default CustomSpeeches;
